import {Injectable} from '@angular/core';

import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, tap} from 'rxjs/operators';

import {ScoreModel} from '@app/core/models/score.model';
import {ScoreType} from '@app/core/models/enumerations/score-type.enum';
import {ScoreService} from '@app/core/services/score/score.service';

@Injectable({providedIn: 'root'})
export class ScoreState {
  private readonly _scores$: BehaviorSubject<ScoreModel[]>;
  private readonly _scoreId$: BehaviorSubject<ScoreType>;

  /**
   * Holds the value of selected score
   * @type {Observable<ScoreModel>}
   */
  public score$: Observable<ScoreModel>;

  /**
   * Holds the id of selected score
   * @type {Observable<ScoreType>}
   */
  public scoreId$: Observable<ScoreType>;

  /**
   * Holds the scores list
   * @type {Observable<ScoreModel[]>}
   */
  public scores$: Observable<ScoreModel[]>;

  public constructor(private readonly _service: ScoreService) {
    this._scores$  = new BehaviorSubject([]);
    this._scoreId$ = new BehaviorSubject(ScoreType.SAT);

    this.scoreId$ = this._scoreId$.asObservable().pipe(distinctUntilChanged());

    this.scores$ = this._scores$.asObservable().pipe(
      distinctUntilChanged(),
      tap(components => {
        if(components.length === 0) {
          this._service.list().subscribe(response => this.updateScores(response));
        }
      }),
      filter(components => Boolean(components.length !== 0)),
      shareReplay(1)
    );

    this.score$ = combineLatest([this._scores$, this._scoreId$]).pipe(
      map(([components, id]) => components.find(component => component.id === id))
    );
  }

  /**
   * Will be used to update the selection
   * @param {ScoreType} componentId
   */
  public updateScoreId(componentId: ScoreType): void {
    this._scoreId$.next(componentId);
  }

  /**
   * Used to populate the observable with socres list
   * @param {ScoreModel[]} components
   */
  private updateScores(components: ScoreModel[]): void {
    this._scores$.next(components);
  }
}
